import classnames from "classnames";
import PropTypes from "prop-types";
import React from "react";

import {
  getNamingOverrides,
  getOverHeadTransitionLabelOverrides,
} from "../../../helpers";
import useOutputReportStore from "../../../store/OutputReport.store";
import { getOutputType } from "../../../utils/helpers/dataTable";

const DataTableHeadings = ({
  name,
  variation,
  tableColumns,
  tableRowHeaderKey,
  scenarioColumns,
  highlightColumns,
}) => {
  const data = useOutputReportStore((state) => state.data);
  const processedHeaders = tableColumns.map((col) => {
    let label = col.columnTitle ? col.columnTitle : col.columntitle;
    if (label === "Model [2]") {
      // eslint-disable-next-line sonarjs/no-duplicate-string
      label = "Sample Representative Portfolio";
    }
    const subtitleMap = {
      "Maintain and Reinvest Proceeds": "Option 0*",
      "Existing Portfolio (Maintain & Reinvest Proceeds)": "Option 0*",
      "Minimize Tax Liability": "Option 1",
      "Balance Tax Liability & Tracking Error": "Option 2",
      "Minimize Tracking Error": "Option 3",
    };
    const subtitle = subtitleMap[label];

    return {
      label,
      subtitle,
      isColHighlighted: highlightColumns.includes(label),
      isColScenario: scenarioColumns.includes(label),
    };
  });
  const { reportType, assetType } = getOutputType(data);

  const handleLabelAsterisk = (label) => {
    switch (label) {
      case "Target Portfolio1":
        return (label.split("1")[0] += "[1]");
      case "Target Portfolio*":
        return (label.split("*")[0] += "[1]");
      case "Full Liquidation of Pre-Existing Portfolio":
        return (label += "*");
      case "Sample Representative Portfolio":
        return (label += "[2]");
      case "Reference Index**":
        return (label.split("**")[0] += "[2]");
      case "Reference Index":
        return (label += "[2]");
      default:
        return label;
    }
  };

  const isSingleCol = tableColumns.length === 1;
  const isDoubleCol = tableColumns.length === 2;

  return (
    <>
      {reportType === "transition" && (
        <tr className="dataTable__row">
          {tableRowHeaderKey && tableRowHeaderKey !== "" ? (
            <th
              rowSpan="2"
              scope="col"
              className="dataTable__cell dataTable__cell-th dataTable__col-header"
            >
              <div className="dataTable__cell-card">
                <div className="dataTable__col-title">{tableRowHeaderKey}</div>
              </div>
            </th>
          ) : (
            <td
              rowSpan="2"
              className="dataTable__cell dataTable__cell-th dataTable__col-header"
            >
              <div className="dataTable__cell-card">
                <div className="dataTable__col-title">{tableRowHeaderKey}</div>
              </div>
            </td>
          )}

          {processedHeaders.map(({ label }, i) => {
            if (label === scenarioColumns[0]) {
              return (
                <th
                  key={i}
                  colSpan="3"
                  scope="colgroup"
                  className="dataTable__col-header dataTable__col--marker"
                >
                  {getOverHeadTransitionLabelOverrides(
                    assetType,
                    reportType,
                    name
                  )}
                </th>
              );
            }

            if (label === scenarioColumns[1] || label === scenarioColumns[2]) {
              return null;
            }

            return (
              <td key={i} className="dataTable__col-header">
                &nbsp;
              </td>
            );
          })}
        </tr>
      )}

      <tr className="dataTable__row">
        {reportType !== "transition" && (
          <>
            {tableRowHeaderKey && tableRowHeaderKey !== "" ? (
              <th
                scope="col"
                className={classnames(
                  "dataTable__cell",
                  "dataTable__cell-th",
                  "dataTable__cell-th--header",
                  {
                    "dataTable__col--single": isSingleCol,
                    "dataTable__col--double": isDoubleCol,
                  }
                )}
              >
                <div className="dataTable__cell-card">
                  <div className="dataTable__col-title">
                    {tableRowHeaderKey}
                  </div>
                </div>
              </th>
            ) : (
              <td className="dataTable__cell dataTable__cell-th dataTable__col-header">
                <div className="dataTable__cell-card">
                  <div className="dataTable__col-title">
                    {tableRowHeaderKey}
                  </div>
                </div>
              </td>
            )}
          </>
        )}

        {processedHeaders.map((header, headerIndex) => (
          <th
            key={headerIndex}
            scope="col"
            className={classnames(
              "dataTable__cell",
              "dataTable__cell-th",
              "dataTable__cell-th--header",
              {
                "dataTable__col--highlighted": header.isColHighlighted,
                "dataTable__col--scenario":
                  variation !== "basic" && header.isColScenario,
              }
            )}
          >
            <div className="dataTable__cell-card">
              {variation !== "basic" && header.subtitle && (
                <div className="dataTable__col-subtitle">
                  {variation !== "max"
                    ? getNamingOverrides(
                        header.label,
                        assetType,
                        reportType,
                        name,
                        "subheading",
                        header.subtitle
                      )
                    : header.subtitle.replace(/Option\s/, "#")}
                </div>
              )}
              <div className="dataTable__col-title">
                {getNamingOverrides(
                  handleLabelAsterisk(header.label),
                  assetType,
                  reportType,
                  name
                )}
              </div>
            </div>
          </th>
        ))}
      </tr>
    </>
  );
};

DataTableHeadings.propTypes = {
  name: PropTypes.string,
  variation: PropTypes.string,
  tableColumns: PropTypes.array,
  tableRowHeaderKey: PropTypes.string,
  scenarioColumns: PropTypes.array,
  highlightColumns: PropTypes.array,
};

DataTableHeadings.defaultProps = {
  variation: "basic",
  tableColumns: [],
  scenarioColumns: [],
  highlightColumns: [],
};

export default DataTableHeadings;
